/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useEffect, useCallback } from 'react';
import { useLazyQuery } from '@apollo/react-hooks';
import { useSelector } from 'react-redux';
import { escapeRegExp } from 'lodash';
import { GET_PRODUCT_LIST } from '../../../graphql/getProductList';
import { GET_PRIMARY_ACCOUNT } from '../../../graphql/getPrimaryAccount';
import { GET_UPDATED_RESOLVED_PRICE } from '../../../graphql/getUpdatedResolvedPrice';
import { getUserInfo } from '../../../store/ducks/userInfo';
import debounced from '../../../util/debounced';
import { ERROR_MESSAGES, TAB_VALUES, NOTIFY_TYPE } from '../../../util/constants';
import useSnackBar from '../../../util/customHooks/useSnackBar';
import { GET_SALES_REPS } from '../../../graphql/getSalesReps';
import {
  GetUpdatedResolvedPriceResponse,
  GetUpdatedResolvedPriceVariables,
} from '../../../interface/ResolvedPrice';

const useData = () => {
  const [selectedAccount, setSelectedAccount] = useState<any>(null);
  const [selectedContract, setSelectedContract] = useState<any>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<any[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
  const [pricingItems, setPricingItems] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loader, setLoader] = useState(false);
  const [searchTerm, setSearchText] = useState('');
  const [productSearchTerm, setProductSearchTerm] = useState<{
    searchTerm: string;
    contract?: any;
  }>({ searchTerm: '' });
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [contractSearchTerm, setContractSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingContractProducts, setLoadingContractProducts] = useState(false);
  const [activeTab, setActiveTab] = useState<'Customer' | 'Contract'>(TAB_VALUES.CUSTOMER);
  const [tempSelectedProducts, setTempSelectedProducts] = useState<any[]>([]);
  const userInfo = useSelector(getUserInfo);
  const { openSnackBar } = useSnackBar();
  const [salesReps, setSalesReps] = useState<any[]>([]);
  const [selectedSalesRepId, setSelectedSalesRepId] = useState<string | null>(null);

  const getUoM = (name: string, packContent: string): string => {
    let actualUOM = name;
    if (packContent && Number(packContent) > 1) {
      actualUOM += `/ ${packContent}`;
    }
    return actualUOM || 'PCE';
  };

  const getActualUoM = (item: any) => {
    const actualUOM = getUoM(item.salesUnitOfMeasure, item.packContent);

    return {
      actualUOM,
    };
  };

  const [getAccounts, { data: accountsData, loading: loadingAccounts }] = useLazyQuery(
    GET_PRIMARY_ACCOUNT,
    {
      fetchPolicy: 'no-cache',
    }
  );

  const [getSalesReps, { data: salesRepsData, loading: loadingSalesReps }] = useLazyQuery(
    GET_SALES_REPS,
    {
      fetchPolicy: 'no-cache',
    }
  );

  const [getProductList, { data: catalogData, loading: loadingCatalog }] = useLazyQuery(
    GET_PRODUCT_LIST,
    {
      fetchPolicy: 'no-cache',
      onError: productError => {
        setLoader(false);

        if (productError?.message) {
          openSnackBar(NOTIFY_TYPE.ERROR, productError.message);
        } else {
          openSnackBar(NOTIFY_TYPE.ERROR, 'Something went wrong. Please try again.');
        }
      },
    }
  );

  const [getUpdatedResolvedPrice, { data: updatedPriceData }] = useLazyQuery<
    GetUpdatedResolvedPriceResponse,
    GetUpdatedResolvedPriceVariables
  >(GET_UPDATED_RESOLVED_PRICE, {
    fetchPolicy: 'no-cache',
    onCompleted: () => {
      setLoader(false);
    },
    onError: rtpError => {
      setLoader(false);
      const errMsg = rtpError?.message?.includes('Error:')
        ? rtpError.message.split('Error:')[1]?.trim()
        : rtpError.message.split(':')[1]?.trim() || 'Something went wrong. Please try again.';

      openSnackBar(NOTIFY_TYPE.ERROR, errMsg);

      const placeholderItems = tempSelectedProducts.map((product: any) => {
        const priceItem = catalogData?.getProductList?.find(
          (item: any) => item.productNumber === product.productNumber
        );
        const { actualUOM } = getActualUoM(product);
        return {
          ...product,
          accountNumber: selectedAccount?.number || 'N/A',
          accountName: selectedAccount?.value || 'N/A',
          floorPrice: product.floorPrice || 0,
          targetPrice: product.targetPrice || 0,
          listPrice: priceItem?.listPrice || product.listPrice || 0,
          salesUnitOfMeasure: product.salesUnitOfMeasure || '—',
          sellingUoM: product.sellingUoM || '—',
          packContent: product.packContent || '',
          startDate: product.resolvedPrice.startDate,
          endDate: product.resolvedPrice.endDate,
          actualUoM: actualUOM,
          lineTotal: product.quantity * (priceItem?.listPrice || 0),
        };
      });
      setPricingItems(placeholderItems);
      setSelectedProducts(tempSelectedProducts);
      setTempSelectedProducts([]);
    },
  });
  const isAgencyPersona = userInfo?.personas?.toLowerCase().includes('agency');

  useEffect(() => {
    if (userInfo?.id) {
      getAccounts({
        variables: {
          salesRepId: userInfo.id,
          limit: 100,
        },
      });
    }
  }, [userInfo]);

  useEffect(() => {
    if (catalogData?.getProductList) {
      const uniqueProducts = Array.from(
        new Set(catalogData.getProductList.map((product: any) => product.productNumber))
      ).map(productNumber => {
        return catalogData.getProductList.find(
          (product: any) => product.productNumber === productNumber
        );
      });
      setProducts(uniqueProducts);
      setFilteredProducts(uniqueProducts);
    }
  }, [catalogData, selectedAccount]);

  const fetchInventory = useCallback(
    debounced(500, (searchText: string) => {
      if (selectedAccount?.number) {
        // Ensure selectedAccount is set
        getProductList({
          variables: {
            limit: 50,
            offset: 0,
            searchText,
            salesRepId: isAgencyPersona ? selectedSalesRepId : userInfo?.id,
            filters: {
              productName: [],
              businessUnit: [userInfo?.businessunit] || ['Sports Med'],
              userId: userInfo?.id,
            },
          },
        });
      }
    }),
    [getProductList, selectedAccount, userInfo]
  );

  const fetchAccounts = useCallback(
    debounced(500, (searchText: string) => {
      getAccounts({
        variables: {
          salesRepId: userInfo?.id,
          limit: 50,
          filters: {
            searchText,
          },
        },
      });
    }),
    [getAccounts, userInfo]
  );

  useEffect(() => {
    if (selectedAccount?.number) {
      if (searchTerm?.trim().length === 0) {
        fetchInventory('');
      } else if (searchTerm.length < 3) {
        fetchInventory('');
      } else {
        fetchInventory(searchTerm);
      }
    }
  }, [searchTerm]);

  useEffect(() => {
    if (isAgencyPersona) {
      getSalesReps({
        variables: {
          userId: userInfo?.id,
        },
      });
    }
  }, [userInfo, getSalesReps]);

  useEffect(() => {
    if (salesRepsData?.getSalesReps) {
      const { getSalesReps: salesRepsList } = salesRepsData;
      setSalesReps(salesRepsList);
      if (getSalesReps.length > 0) {
        setSelectedSalesRepId(salesRepsList[0].id);
      }
    }
  }, [salesRepsData]);

  useEffect(() => {
    fetchAccounts(accountSearchTerm);
  }, [accountSearchTerm]);

  const updatePricingItems = useCallback(() => {
    if (updatedPriceData?.getUpdatedResolvedPrice) {
      const updatedItems = selectedProducts.map((product: any) => {
        const priceItem = updatedPriceData.getUpdatedResolvedPrice.find(
          (price: any) => price.productNumber === product.productNumber
        );
        const { actualUOM } = getActualUoM(product);
        return {
          ...product,
          contractPrice: priceItem?.resolvedPrice,
          contractName: priceItem?.contractName ?? product.contractName,
          listPrice: priceItem?.listPrice ?? product.listPrice ?? product.productCost,
          floorPrice: product.floorPrice,
          targetPrice: product.targetPrice,
          sellingUoM: priceItem?.sellingUOM,
          actualUoM: actualUOM,
          startDate: product.resolvedPrice.startDate || '—',
          endDate: product.resolvedPrice.endDate || '—',
          salesUnitOfMeasure: product.salesUnitOfMeasure,
          packContent: product.packContent,
          lineTotal: Number(product.quantity) * Number(priceItem?.resolvedPrice || 0),
          accountNumber: selectedAccount?.number || 'N/A',
          accountName: selectedAccount?.value || 'N/A',
          discountableFlag: priceItem?.discountableFlag || '—',
        };
      });
      setPricingItems(updatedItems);
      setSelectedProducts(tempSelectedProducts);
      setTempSelectedProducts([]);
    }
  }, [updatedPriceData]);

  useEffect(() => {
    updatePricingItems();
  }, [updatedPriceData, updatePricingItems]);

  const handleAccountSelect = (event: any, data: any) => {
    if (data.selection && data.selection.length > 0) {
      const account = data.selection[0];
      setSelectedAccount(account);
      setProducts([]);
      setFilteredProducts([]);
      if (account?.number) {
        getProductList({
          variables: {
            limit: 50,
            offset: 0,
            salesRepId: isAgencyPersona ? selectedSalesRepId : userInfo?.id,
            searchText: '',
            filters: {
              productName: [],
              businessUnit: [userInfo?.businessunit] || ['Sports Med'],
              userId: userInfo?.id,
            },
          },
        });
      }
    } else {
      setSelectedAccount(null);
      setProducts([]);
      setFilteredProducts([]);
    }
  };

  const handleSetItems = (items: any[]) => {
    setPricingItems(items);
    setSelectedProducts(items);
  };

  const handleProductAdd = () => {
    if (!selectedAccount) {
      setError(ERROR_MESSAGES.SELECT_ACCOUNT_OR_CONTRACT);
      return;
    }
    setLoader(true);
    setTempSelectedProducts(selectedProducts);
    getUpdatedResolvedPrice({
      variables: {
        productNumbers: selectedProducts.map(product => product.productNumber),
        accountNumber: selectedAccount?.number || selectedProducts[0].accountNumber,
      },
    });
  };

  const handleQuantityChange = (productId: string, quantity: number) => {
    setSelectedProducts(prevItems => {
      const existingItem = prevItems.find(item => item.productId === productId);
      if (existingItem) {
        return prevItems
          .map(item => (item.productId === productId ? { ...item, quantity } : item))
          .filter(item => item.quantity > 0);
      }
      const product = products.find(p => p.productId === productId);
      return [...prevItems, { ...product, quantity }];
    });
  };

  const handleReset = () => {
    setSelectedAccount(null);
    setSelectedContract(null);
    setSearchText('');
    setAccountSearchTerm('');
    setProductSearchTerm({ searchTerm: '', contract: null });
    setContractSearchTerm('');
    setFilteredProducts([]);
    setSelectedProducts([]);
    setPricingItems([]);
    setProducts([]);
    setError(null);
  };

  const Reset = () => {
    setSelectedAccount(null);
    setSelectedContract(null);
    setSearchText('');
    setAccountSearchTerm('');
    setProductSearchTerm({ searchTerm: '', contract: null });
    setContractSearchTerm('');
    setFilteredProducts([]);
    setSelectedProducts([]);
    setPricingItems([]);
    setProducts([]);
    setError(null);
  };
  const clearProducts = () => {
    setFilteredProducts([]);
    setSelectedProducts([]);
    setPricingItems([]);
  };
  const accountOptions = accountsData?.getPrimaryAccount
    .filter((account: any) => !account.isITorIHAccount && !account.isRepStockAccount)
    .map((account: any) => ({
      id: account.id,
      label: `${account.value} (${account.number})`,
      ...account,
    }));

  const filteredAccountOptions = accountOptions?.filter((account: any) => {
    const inputValueRegExp = new RegExp(escapeRegExp(accountSearchTerm), 'ig');
    const searchTermFound = account.label ? account.label.match(inputValueRegExp) : false;
    return searchTermFound;
  });

  return {
    selectedAccount,
    selectedContract,
    filteredProducts,
    selectedProducts,
    pricingItems,
    error,
    isModalOpen,
    loader,
    loadingAccounts,
    loadingCatalog,
    loadingContractProducts,
    filteredAccountOptions,
    handleAccountSelect,
    handleSetItems,
    handleProductAdd,
    handleQuantityChange,
    setSearchText,
    setAccountSearchTerm,
    setContractSearchTerm,
    setIsModalOpen,
    setSelectedAccount,
    setSelectedContract,
    handleReset,
    Reset,
    clearProducts,
    setFilteredProducts,
    setProductSearchTerm,
    setActiveTab,
    getUoM,
  };
};

export default useData;
